import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";   // ✅ ADD THIS
import { useAuth } from './context/AuthContext';
import { PWAProvider } from './context/PWAContext';

// --- Layouts ---
import PublicLayout from './components/layouts/PublicLayout';
import AdminLayout, { AdminProtectedRoute } from './pages/admin/AdminLayout';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';

// --- Page Components ---
import HomePage from './pages/HomePage';
import AuditionDetailPage from './pages/AuditionDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardEntryPoint from './pages/dashboard/DashboardEntryPoint';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
import BrowseRoles from './pages/BrowseRoles';
import AboutUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import BannerSlider from './components/BannerSlider';
import ContactUs from './pages/ContactUs';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';

// --- Admin Components ---
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersList from './pages/admin/AdminUsersList';
import AdminProjectList from './pages/admin/AdminProjectList';
import AdminRoleList from './pages/admin/AdminRoleList';
import AdminLookupPage from './pages/admin/AdminLookupPage';
import PostProjectsPage from './pages/projects/PostProjectsPage';
import AddEditRolePage from './pages/roles/AddEditRolePage';
import NotificationTemplates from './pages/admin/NotificationTemplates';
import AdminContactSubmissions from './pages/admin/AdminContactSubmissions';
import AdminAuditionsList from './pages/admin/AdminAudtionsList';
import ManageApplicationDetails from './pages/roles/ManageApplicationDetails';
import AdminNotificationLogs from './pages/admin/AdminNotificationLogs';
import AdminCostPage from './pages/admin/AdminCostPage';
import CouponRedeem from './pages/admin/CouponRedeem';
import AdminTestimonials from './pages/admin/AdminTestimonials';
import MyProfilePage from './pages/dashboard/talent/MyProfilePage';
import DirectorProfilePage from './pages/dashboard/directorproducer/DirectorProfilePage';
import CastingDirectorProfilePage from './pages/dashboard/castingdirector/CastingDirectorProfilePage';
import CastingDirectorProfile from './components/audition-booking-wizard/CastingDirectorProfile';
import CastingDirectorCard from './components/audition-booking-wizard/CastingDirectorCard';
import PaymentGatewayConfig from './pages/admin/PaymentGatewayConfig';
import AdminPaymentLogs from './pages/admin/AdminPaymentLogs';
import MeetingPage from './pages/MeetingPage';


import './App.css';
// --- Protected Route Component ---
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  if (authLoading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><p>Loading application state...</p></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const AppContent = () => {
  return (
    <>
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <PWAInstallPrompt /> {/* <--- ADD IT HERE SO IT OVERLAYS THE APP */}
      <Routes>
        
        {/* 🔴 STANDALONE ROUTES (Pages that manage their own layouts/headers) */}
        
        {/* 🟢 PUBLIC LAYOUT ROUTES (Automatically get Header & Footer) */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/roles" element={<BrowseRoles />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/audition/:id/:urlSlug" element={<AuditionDetailPage />} />
          <Route path="/banner-slider" element={<BannerSlider />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="casting-director-profile/:id" element={<CastingDirectorProfile />} />
            <Route path="casting-director-card" element={<CastingDirectorCard />} />
            <Route path="/meeting/:roomName" element={<MeetingPage />} />

        </Route>

 {/* <Route
  path="/director-profile"
  element={<PublicCastingDirectorProfile />}
/> */}
        {/* 🔵 USER DASHBOARD (Assuming DashboardEntryPoint has its own Sidebar Layout) */}
        <Route path="/dashboard/*" element={<ProtectedRoute><DashboardEntryPoint /></ProtectedRoute>} />

        {/* 🟣 ADMIN ROUTES */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="candidates" element={<AdminUsersList roleCode="300" title="Candidate Management" />} />
          <Route path="directors" element={<AdminUsersList roleCode="200" title="Director & Producer Management" />} />
          <Route path="casting-directors" element={<AdminUsersList roleCode="400" title="Casting Director Management" />} />
          {/* <Route path="/profile" element={<CastingDirectorProfilePage />} /> */}
          {/* <Route path="/profile" element={<DirectorProfilePage />} /> */}
          <Route path="projects" element={<AdminProjectList roleCode="400" title="Project Management" />} />
          <Route path="roles" element={<AdminRoleList roleCode="400" title="Role Management" />} />
          <Route path="lookups" element={<AdminLookupPage />} />
          <Route path="post-projects" element={<PostProjectsPage />} />
          <Route path="post-projects/:projectId" element={<PostProjectsPage />} /> 
          <Route path="post-roles" element={<AddEditRolePage />} />
          <Route path="post-roles/:id" element={<AddEditRolePage />} /> 
          <Route path="notifications" element={<NotificationTemplates />} />
          <Route path="contact-list" element={<AdminContactSubmissions />} />
          <Route path="audition-list" element={<AdminAuditionsList />} />
          <Route path="manage-applications/:id" element={<ManageApplicationDetails />} />
          <Route path="notification-logs" element={<AdminNotificationLogs />} />
          <Route path="cost-page" element={<AdminCostPage />} />
          <Route path="coupon-redeem" element={<CouponRedeem />} />
          <Route path="testimonial" element={<AdminTestimonials />} />
          <Route path="candidate-profile/add" element={<MyProfilePage />} />
          <Route path="candidate-profile/:id" element={<MyProfilePage />} />
          <Route path="director-profile/add" element={<DirectorProfilePage />} />
          <Route path="director-profile/:id" element={<DirectorProfilePage />} />
          <Route path="casting-profile/add" element={<CastingDirectorProfilePage />} />
          <Route path="casting-profile/:id" element={<CastingDirectorProfilePage />} />
          <Route
  path="/admin/payment-gateway"
  element={<PaymentGatewayConfig />}
/>
<Route
  path="/admin/payment-logs"
  element={<AdminPaymentLogs />}
/>
         

         
        </Route>

        {/* 404 Route */}
        <Route path="*" element={
          <div style={{ padding: "2rem", textAlign: "center", minHeight: '70vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <h2>404 - Page Not Found</h2>
            <Link to="/" className="btn btn-primary mt-3">Go to Homepage</Link>
          </div>
        } />
      </Routes>
    </>
  );
};

function App() {
  return (
    <Router>
      <PWAProvider> {/* ✅ WRAP APP WITH PROVIDER */}
        <AppContent />
      </PWAProvider>
    </Router>
  );
}

export default App;